// src/app/layouts/AppLayout.tsx
import { Outlet, useLocation } from '@tanstack/react-router';
import { Toaster } from '@/components/ui/sonner';
import { FeatureErrorBoundary } from '@/shared/components/FeatureErrorBoundary';
import { ErrorBoundaryFallback } from '@/shared/components/ErrorBoundaryFallback';
import { useApplyTheme } from '@/shared/hooks/useTheme';

export const AppLayout = () => {
  const { pathname } = useLocation();
  // Apply the persisted theme (light / dark / system) to the document root.
  // The app ships a full `.dark` token set in styles.css that nothing was
  // toggling; this + the profile Dark/Light Mode item restore working themes.
  useApplyTheme();

  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      {/* Root safety net. AuthLayout wraps its own <Outlet/> in a boundary, but
          that only covers routes rendered INSIDE the authenticated chrome —
          anything that throws in AuthLayout itself (profile fetch, sidebar,
          Soteria panel) or on a route outside it (login, onboarding, accept
          invitation) had nothing above it and took the whole app down to a
          blank white page with no way back except a manual reload.

          The Toaster is deliberately left OUTSIDE the boundary so toasts keep
          rendering even while the fallback is showing. */}
      <FeatureErrorBoundary
        // Navigating away clears the caught error. Without this the fallback
        // is sticky: it holds the error in state, so every later route rendered
        // the same card and the sidebar looked dead until a manual reload.
        resetKey={pathname}
        // The default fallback is sized `h-full`, which collapses here because
        // the wrapper only sets `min-h-screen` — at the root we want the error
        // card centred in the viewport, so pass an explicitly sized fallback.
        fallback={(error, reset) => (
          <ErrorBoundaryFallback
            error={error}
            reset={reset}
            className="min-h-screen"
          />
        )}
      >
        <Outlet />
      </FeatureErrorBoundary>

      {/* closeButton: validation toasts can be multi-line / multi-paragraph
          (wizard preflight surfaces every BacktestConfig error inline) and
          users were getting stuck staring at a wall of text they couldn't
          dismiss. The × also helps when a toast lands behind a dialog. */}
      {/* BOTTOM RIGHT, not the top centre. A toast at the top middle lands on
        the thing the reader was looking at — the page heading, the row they
        just clicked — and every one of them, success or failure, interrupted
        the same spot. The bottom right is out of the reading path and is where
        a status message is looked for. */}
      <Toaster position="bottom-right" closeButton richColors />
      {/* Router + React Query devtools removed — their fixed bottom-bar /
          toggle overlays drew a white band across the app (dev-only, but
          visually noisy). Re-add locally if you need to debug. */}
    </div>
  );
};
